// capture-extract.ts — pure functions for Phase 2.1 smart capture.
// buildCaptureRequest: assembles the AiGenerateRequest (no network).
// mapCaptureResult: validates + maps raw CaptureResult → ClarifyPrefill + type.
// No side effects; fully unit-testable without mocking fetch.

import type { AiGenerateRequest } from "./ai-client";
import { CAPTURE_RESPONSE_SCHEMA } from "./capture-schema";
import type { CaptureResult } from "./capture-schema";
import type { ClarifyPrefill } from "../../bento/inbox-clarify-steps";
import type { InboxConversionType } from "../../types";

// ── Constants ─────────────────────────────────────────────────────────────────

const VALID_TYPES = new Set<string>(["task", "project", "goal", "note", "habit", "event"]);
const VALID_PRIORITIES = new Set<string>(["low", "medium", "high"]);
const VALID_HORIZONS = new Set<string>(["quarter", "year", "life"]);
// YYYY-MM-DD basic check (no full calendar validation needed — just format)
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^\d{2}:\d{2}$/;

// ── buildCaptureRequest ───────────────────────────────────────────────────────

/**
 * Assemble the AiGenerateRequest for inbox-text extraction.
 * PURE — no network, no side effects.
 *
 * @param text     Raw inbox text entered by the user.
 * @param todayIso Today's date in YYYY-MM-DD (used to resolve relative dates).
 */
export function buildCaptureRequest(
  text: string,
  todayIso: string,
): AiGenerateRequest {
  const prompt = `Hôm nay là ${todayIso}. Hãy phân tích đoạn văn bản inbox dưới đây và trả về JSON theo schema được chỉ định.

Quy tắc GTD:
- task: hành động đơn lẻ, có thể hoàn thành trong một bước.
- project: kết quả cần nhiều bước (≥2 hành động liên tiếp).
- someday: ý tưởng/mong muốn chưa cần làm ngay (wishlist, "một ngày nào đó").
- reference: thông tin cần lưu lại để tham khảo, không cần hành động.

Quy tắc type:
- task / event → gtd=task (event nếu có thời điểm cụ thể như giờ họp, hẹn gặp).
- project → gtd=project.
- goal → gtd=project (mục tiêu dài hạn cần nhiều bước).
- habit → gtd=task (hành động lặp lại hằng ngày/tuần).
- note → gtd=reference.
- someday/reference → type="note" (để lưu trữ).

Ngày tương đối (hôm nay, ngày mai, tuần sau, v.v.) phải được quy đổi thành ngày tuyệt đối YYYY-MM-DD dựa vào hôm nay là ${todayIso}.

Khi type=goal, trích thêm:
- horizon: "year" nếu trong năm nay/"cuối năm", "quarter" nếu trong vài tháng/một quý, "life" nếu dài hạn/cả đời.
- metric: đơn vị/chỉ số đo lường (vd "Triệu VNĐ", "kg", "cuốn sách", "%").
- target: con số mục tiêu (vd "tích luỹ 100 triệu" → target=100, metric="Triệu VNĐ").
- dueDate: nếu có mốc "cuối năm nay" thì đặt 31/12 của năm tương ứng.
Ví dụ: "cuối năm nay tích luỹ 100tr" → type=goal, horizon="year", metric="Triệu VNĐ", target=100, dueDate="${todayIso.slice(0, 4)}-12-31".

Văn bản inbox:
"""
${text}
"""

Chỉ trả về JSON hợp lệ theo schema — không giải thích thêm.`;

  return {
    contents: [{ role: "user", parts: [{ text: prompt }] }],
    generationConfig: {
      maxOutputTokens: 512, // headroom: JSON đủ field + reason tiếng Việt, tránh cắt cụt
      temperature: 0.1,
    },
    responseSchema: CAPTURE_RESPONSE_SCHEMA,
  };
}

// ── mapCaptureResult ──────────────────────────────────────────────────────────

/**
 * Validate and map a raw CaptureResult (possibly garbage) to a safe
 * { prefill, type } pair.  Never throws — bad/missing fields fall back
 * to safe defaults.
 *
 * Mapping rules (per spec):
 *   - type not in valid set → "task"
 *   - gtd "reference" → type "note"
 *   - priority not in valid set → "medium"
 *   - estimateMinutes out of [5,480] → clamp; non-number → omit
 *   - dueDate/time malformed → omit
 *   - tags must be string[] → filter non-strings; absent → omit
 */
export function mapCaptureResult(raw: CaptureResult): {
  prefill: ClarifyPrefill;
  type: InboxConversionType;
} {
  // ── type resolution ──────────────────────────────────────────────────────
  let type: InboxConversionType =
    typeof raw?.type === "string" && VALID_TYPES.has(raw.type)
      ? (raw.type as InboxConversionType)
      : "task";

  // gtd "reference" overrides type → note
  if (raw?.gtd === "reference") {
    type = "note";
  }

  // ── title ────────────────────────────────────────────────────────────────
  const title =
    typeof raw?.title === "string" && raw.title.trim()
      ? raw.title.trim()
      : undefined;

  // ── dueDate ──────────────────────────────────────────────────────────────
  const dueDate =
    typeof raw?.dueDate === "string" && DATE_RE.test(raw.dueDate)
      ? raw.dueDate
      : undefined;

  // ── time ─────────────────────────────────────────────────────────────────
  const time =
    typeof raw?.time === "string" && TIME_RE.test(raw.time)
      ? raw.time
      : undefined;

  // ── priority ─────────────────────────────────────────────────────────────
  const priority: "low" | "medium" | "high" =
    typeof raw?.priority === "string" && VALID_PRIORITIES.has(raw.priority)
      ? (raw.priority as "low" | "medium" | "high")
      : "medium";

  // ── estimateMinutes (clamp 5–480, round; non-number → omit) ───────────────
  let estimateMinutes: number | undefined;
  if (typeof raw?.estimateMinutes === "number" && Number.isFinite(raw.estimateMinutes)) {
    estimateMinutes = Math.min(480, Math.max(5, Math.round(raw.estimateMinutes)));
  }

  // ── tags ─────────────────────────────────────────────────────────────────
  let tags: string[] | undefined;
  if (Array.isArray(raw?.tags)) {
    const filtered = raw.tags.filter((t): t is string => typeof t === "string" && t.trim() !== "");
    if (filtered.length > 0) tags = filtered;
  }

  // ── goal-specific (horizon / metric / target) ─────────────────────────────
  const horizon: "quarter" | "year" | "life" | undefined =
    typeof raw?.horizon === "string" && VALID_HORIZONS.has(raw.horizon)
      ? (raw.horizon as "quarter" | "year" | "life")
      : undefined;
  const metric =
    typeof raw?.metric === "string" && raw.metric.trim() ? raw.metric.trim() : undefined;
  const target =
    typeof raw?.target === "number" && Number.isFinite(raw.target) && raw.target > 0
      ? raw.target
      : undefined;

  // ── assemble prefill (only defined fields) ────────────────────────────────
  const prefill: ClarifyPrefill = {};
  if (title !== undefined) prefill.title = title;
  if (dueDate !== undefined) prefill.dueDate = dueDate;
  if (time !== undefined) prefill.time = time;
  if (priority !== undefined) prefill.priority = priority;
  if (tags !== undefined) prefill.tags = tags;
  if (estimateMinutes !== undefined) prefill.estimateMinutes = estimateMinutes;
  if (horizon !== undefined) prefill.horizon = horizon;
  if (metric !== undefined) prefill.metric = metric;
  if (target !== undefined) prefill.target = target;

  return { prefill, type };
}

// ── Re-export types for convenience ──────────────────────────────────────────
export type { CaptureResult };
